import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  VEHICLE_STATES, STATUS_DOT, StatusBadge,
} from "@/lib/vehicleStatus";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Search, Loader2, ChevronRight, Trash2 } from "lucide-react";

function StatChip({ label, value, dot }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white shadow-sm px-4 py-3"
      data-testid={`fleet-stat-${label}`}>
      {dot && <span className="h-2.5 w-2.5 rounded-full" style={{ background: dot }} />}
      <div className="leading-tight">
        <p className="text-2xl font-extrabold tracking-tight text-brand-graphite">{value}</p>
        <p className="text-xs font-medium text-brand-gray/60">{label}</p>
      </div>
    </div>
  );
}

function VehicleFormSheet({ open, onOpenChange, onSaved }) {
  const empty = {
    unit: "", type: "SUV", make: "Volkswagen", model: "Atlas", year: "",
    vin: "", plate: "", odometer: "", status: "Disponible", location: "Weston, FL",
    color: "", plate_old: "", lease_fee: "", purchase_date: "", purchase_amount: "",
    payment_method: "", title: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.unit.trim() || !form.plate.trim() || !form.year) {
      return toast.error("Completa Unidad, Placa y Año");
    }
    setSaving(true);
    try {
      await api.post("/vehicles", {
        ...form, year: Number(form.year), odometer: Number(form.odometer || 0),
        lease_fee: Number(form.lease_fee || 0), purchase_amount: Number(form.purchase_amount || 0),
      });
      toast.success("Vehículo agregado");
      setForm(empty);
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("No se pudo guardar el vehículo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="vehicle-form-sheet">
        <SheetHeader><SheetTitle>Agregar vehículo</SheetTitle></SheetHeader>
        <div className="space-y-4 py-5">
          {[
            ["Unidad", "unit", "text", "C4Y20"],
            ["Tipo de vehículo", "type", "text", "SUV"],
            ["Marca", "make", "text", "Volkswagen"],
            ["Modelo", "model", "text", "Atlas"],
            ["Año", "year", "number", "2022"],
            ["Color", "color", "text", "Negro"],
            ["VIN", "vin", "text", "1V2..."],
            ["Placa", "plate", "text", "C4Y20"],
            ["Placa anterior", "plate_old", "text", "82AWAC"],
            ["Odómetro", "odometer", "number", "50000"],
            ["Fecha de compra", "purchase_date", "text", "10/05/2022"],
            ["Monto de compra", "purchase_amount", "number", "38000"],
            ["Forma de pago", "payment_method", "text", "Lease / Cheque"],
            ["Cuota (lease)", "lease_fee", "number", "1241.87"],
            ["Título", "title", "text", "Pagado"],
          ].map(([label, key, type, ph]) => (
            <div className="space-y-1.5" key={key}>
              <Label>{label}</Label>
              <Input type={type} value={form[key]} placeholder={ph}
                data-testid={`vf-${key}`} onChange={set(key)} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="vf-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VEHICLE_STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Ubicación</Label>
            <Input value={form.location} data-testid="vf-location" onChange={set("location")} />
          </div>
        </div>
        <SheetFooter className="flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vf-cancel"
            className="flex-1">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="vf-save"
            className="flex-1 bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar vehículo"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default function Fleet() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fYear, setFYear] = useState("all");
  const [fLoc, setFLoc] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
    api.get("/fleet/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const years = useMemo(
    () => [...new Set((vehicles || []).map((v) => v.year))].sort((a, b) => b - a), [vehicles]);
  const locations = useMemo(
    () => [...new Set((vehicles || []).map((v) => v.location))].filter(Boolean), [vehicles]);

  const filtered = useMemo(() => {
    if (!vehicles) return [];
    const term = q.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (fStatus !== "all" && v.status !== fStatus) return false;
      if (fYear !== "all" && String(v.year) !== fYear) return false;
      if (fLoc !== "all" && v.location !== fLoc) return false;
      if (!term) return true;
      return [v.unit, v.plate, v.vin, v.make, v.model].join(" ").toLowerCase().includes(term);
    });
  }, [vehicles, q, fStatus, fYear, fLoc]);

  const del = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este vehículo?")) return;
    await api.delete(`/vehicles/${id}`);
    toast.success("Vehículo eliminado");
    load();
  };

  if (!vehicles || !stats) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-brand-graphite" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="fleet-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-brand-graphite">Flota</h1>
          <p className="text-brand-gray/70 mt-0.5 text-sm">
            Administra todos los vehículos de tu empresa.
          </p>
        </div>
        <button onClick={() => setSheetOpen(true)} data-testid="add-vehicle-button"
          className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2.5 rounded-md bg-brand-yellow text-brand-graphite hover:bg-[#e6ac00] transition-colors">
          <Plus className="h-4 w-4" /> Agregar vehículo
        </button>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <StatChip label="Total vehículos" value={stats.total} />
        <StatChip label="Disponibles" value={stats.by_status.Disponible} dot={STATUS_DOT.Disponible} />
        <StatChip label="Alquilados" value={stats.by_status.Alquilado} dot={STATUS_DOT.Alquilado} />
        <StatChip label="Reservados" value={stats.by_status.Reservado} dot={STATUS_DOT.Reservado} />
        <StatChip label="Mantenimiento" value={stats.by_status.Mantenimiento} dot={STATUS_DOT.Mantenimiento} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="fleet-search"
            placeholder="Buscar por unidad, placa, VIN, marca o modelo…"
            className="pl-9 h-10" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-[150px] h-10" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {VEHICLE_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fYear} onValueChange={setFYear}>
            <SelectTrigger className="w-[110px] h-10" data-testid="filter-year"><SelectValue placeholder="Año" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los años</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fLoc} onValueChange={setFLoc}>
            <SelectTrigger className="w-[150px] h-10" data-testid="filter-location"><SelectValue placeholder="Ubicación" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las ubicaciones</SelectItem>
              {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabla (desktop) */}
      <div className="hidden lg:block bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-brand-gray/50 bg-gray-50">
                <th className="px-4 py-3 font-semibold">Unidad</th>
                <th className="px-4 py-3 font-semibold">Vehículo</th>
                <th className="px-4 py-3 font-semibold">Color</th>
                <th className="px-4 py-3 font-semibold">Año</th>
                <th className="px-4 py-3 font-semibold">Odómetro</th>
                <th className="px-4 py-3 font-semibold">VIN</th>
                <th className="px-4 py-3 font-semibold">Placa</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-4 py-3 font-semibold">Próx. devolución</th>
                <th className="px-4 py-3 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((v, i) => (
                <tr key={v.id} onClick={() => navigate(`/fleet/${v.id}`)}
                  className="hover:bg-brand-light/50 transition-colors cursor-pointer"
                  data-testid={`vehicle-row-${i}`}>
                  <td className="px-4 py-3 font-bold text-brand-graphite">{v.unit}</td>
                  <td className="px-4 py-3 text-brand-gray">{v.make} {v.model}</td>
                  <td className="px-4 py-3 text-brand-gray">{v.color || "—"}</td>
                  <td className="px-4 py-3 text-brand-gray">{v.year}</td>
                  <td className="px-4 py-3 text-brand-gray">{v.odometer.toLocaleString()} mi</td>
                  <td className="px-4 py-3 text-brand-gray/70 font-mono text-xs">{v.vin || "—"}</td>
                  <td className="px-4 py-3 font-mono font-semibold text-brand-graphite">{v.plate}</td>
                  <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                  <td className="px-4 py-3 text-brand-gray">{v.next_return || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={(e) => del(e, v.id)} data-testid={`delete-vehicle-${i}`}
                        className="p-1.5 rounded-md text-brand-gray/50 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <span className="p-1.5 rounded-md text-brand-gray/50">
                        <ChevronRight className="h-4 w-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-brand-gray/50">
                  No se encontraron vehículos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tarjetas (móvil / tablet) */}
      <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filtered.map((v, i) => (
          <div key={v.id} onClick={() => navigate(`/fleet/${v.id}`)}
            className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 cursor-pointer active:scale-[0.99] transition-transform"
            data-testid={`vehicle-card-${i}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-extrabold text-brand-graphite">{v.unit}</p>
                <p className="text-sm text-brand-gray">{v.make} {v.model} · {v.year}</p>
              </div>
              <StatusBadge status={v.status} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-brand-gray">
              <span>Placa: <b className="text-brand-graphite font-mono">{v.plate}</b></span>
              <span>Odóm.: <b className="text-brand-graphite">{v.odometer.toLocaleString()} mi</b></span>
              <span className="col-span-2">Próx. devolución: <b className="text-brand-graphite">{v.next_return || "—"}</b></span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-brand-gray/50 py-8 col-span-full">No se encontraron vehículos.</p>
        )}
      </div>

      <VehicleFormSheet open={sheetOpen} onOpenChange={setSheetOpen} onSaved={load} />
    </div>
  );
}
